import api from "@/api";
import { addHighlights } from "@/services/highlighter";
import notify from "@/utils/notify";
import globals from "@/utils/globals";

const SPEED_OPTIONS = [
  {
    label: "0.75×",
    value: 0.75
  },
  {
    label: "1.0×",
    value: 1.0
  },
  {
    label: "1.5×",
    value: 1.5
  },
  {
    label: "1.75×",
    value: 1.75
  },
  {
    label: "2.0×",
    value: 2.0
  }
];

const state = {
  book: {
    id: null,
    slug: null,
    loading: false,
    error: null,
    cover: null,
    title: null,
    author: null,
    isFreeDaily: false,
    chapters: []
  },
  audio: {
    url: null,
    playing: false,
    loading: false,
    error: null,
    currentTime: 0,
    displayTime: 0,
    volume: 1,
    totalTime: 0,
    speed: {
      options: SPEED_OPTIONS,
      selectedSpeed: 1
    },
    player: null
  },
  chapter: {
    id: null,
    loading: false,
    changing: false,
    error: null,
    title: null,
    text: null,
    audioUrl: null
  },
  highlights: [],
  fontSize: { key: "small", value: "text-r2" }

};

const mutations = {
  book: (state, payload) => {
    state.book = {
      ...state.book,
      ...payload
    };
  },
  audio: (state, payload) => {
    state.audio = {
      ...state.audio,
      ...payload
    };
  },
  chapter: (state, payload) => {
    state.chapter = {
      ...state.chapter,
      ...payload
    };
  },
  setSpeed: (state, speed) => {
    state.audio.speed.selectedSpeed = speed;
  },
  toggleAudioState(state) {
    state.audio.playing = !state.audio.playing;
  },
  play(state) {
    state.audio.playing = true;
  },
  pause(state) {
    state.audio.playing = false;
  },
  setCurrentTime(state, currentTime) {
    state.audio.currentTime = currentTime;
  },
  setTotalTime(state, totalTime) {
    state.audio.totalTime = totalTime;
  },
  setDisplayTime(state, displayTime) {
    state.audio.displayTime = displayTime;
  },
  setVolume(state, volume) {
    state.audio.volume = volume;
  },
  setFontSize(state, fontSize) {
    state.fontSize = fontSize;
  },
  setHighlights(state, highlights) {
    state.highlights = highlights;
  },
  addTemporaryHighlight(state, highlight) {
    state.highlights.push({
      "chapter_id": state.chapter.id,
      _id: "temp",
      ...highlight
    });
  },
  removeHighlightTemporariliy(state, textMarkerId) {
    const newHighlights = state.highlights.filter((el) => {
      return el._id !== textMarkerId;
    });

    state.highlights = newHighlights;
  }
};

const getters = {
  chapter: (state) => {
    let chapter = { ...state.chapter };
    chapter.text = addHighlights(chapter, state.highlights);
    return chapter;
  },
  currentChapter: (state, getters) => {
    const chapter = getters.chapter;
    return chapter;
  },
  isFreeDaily(state) {
    return state.book.isFreeDaily;
  },
  bookSlug(state) {
    return state.book.slug;
  },
  keyIdeas: (state) => {
    return state.book.chapters.map(c => ({
      title: c.action_title,
      id: c.id,
      order: c.order_no
    }));
  },
  chapterById: (state) => id => {
    return state.book.chapters.find(a => a.id === id);
  },
  chapterIndex: state => id => {
    return state.book.chapters.findIndex(a => a.id === id);
  },
  nextChapter: (state, getters) => {
    const index = getters.chapterIndex(state.chapter.id);
    return index !== null && state.book.chapters[index + 1];
  },
  previousChapter: (state, getters) => {
    const index = getters.chapterIndex(state.chapter.id);
    return index !== null && state.book.chapters[index - 1];
  },
  currentChapterIndex: (state, getters) => {
    return getters.chapterIndex(state.chapter.id);
  },
  isFirstChapter(state, getters) {
    const currentChapterIndex = getters.chapterIndex(state.chapter.id);

    return currentChapterIndex === 0;
  },
  isLastChapter(state, getters) {
    const currentChapterIndex = getters.chapterIndex(state.chapter.id);

    return currentChapterIndex === state.book.chapters.length - 1;
  },
  audioUrl: (state) => {
    return state.chapter.audioUrl;
  },
  isAudioPlaying: (state) => {
    return state.audio.playing;
  },
  currentTime: (state) => {
    return state.audio.currentTime;
  },
  displayTime: (state) => {
    return state.audio.displayTime;
  },
  totalTime: (state) => {
    return state.audio.totalTime;
  },
  speedOptions: (state) => {
    return state.audio.speed.options;
  },
  selectedSpeed: (state) => {
    return state.audio.speed.selectedSpeed;
  },
  selectedSpeedLabel: (state, getters) => {
    return getters.speedOptions.find(o => o.value === getters.selectedSpeed).label;
  },
  volume: (state) => {
    return state.audio.volume;
  },
  fontSize: (state) => {
    return state.fontSize;
  }
};

const actions = {
  play: ({ commit }) => {
    commit("play");
  },
  pause: ({ commit }) => {
    commit("pause");
  },
  toggleAudioState: ({ commit }) => {
    commit("toggleAudioState");
  },
  setCurrentTime: ({ commit }, currentTime) => {
    commit("setCurrentTime", currentTime);
  },
  toggleMobileAudioExpanded: ({ commit }) => {
    commit("toggleMobileAudioExpanded");
  },
  setDisplayTime: ({ commit }, time) => {
    commit("setDisplayTime", time);
  },
  setTotalTime: ({ commit }, totalTime) => {
    commit("setTotalTime", totalTime);
  },
  setVolume: ({ commit }, volume) => {
    commit("setVolume", volume);
  },
  skipForwards: ({ getters, commit, dispatch }) => {
    if (getters.displayTime < (getters.totalTime - 15)) {
      commit("setCurrentTime", getters.displayTime + 15);

    } else {
      dispatch("chapter", getters.nextChapter.id);
    }
  },
  // If we have the audio updating and watching the same variable, it
  // jitters as it's constantly updating itself. I split the variables into
  // two, one to listen to and one to update. However, now the one it
  // listens to isn't updated, so setting it to 0 twice only causes on
  // restart, hence this hack
  skipBackwards: ({ getters, commit }) => {
    if (getters.displayTime <= 15) {
      let newTime = getters.currentTime === 0 ? 0.1 : 0;

      commit("setCurrentTime", newTime);

    } else {
      commit("setCurrentTime", getters.displayTime - 15);
    }
  },

  setSpeedToNextSpeed: ({ getters, dispatch }) => {
    const currentSpeed = getters.selectedSpeed;
    const options = getters.speedOptions;
    const indexOfCurrentSpeed = options.findIndex(o => o.value === currentSpeed);

    let newSpeedIndex = null;

    if (indexOfCurrentSpeed === options.length - 1) {
      newSpeedIndex = 0;
    } else {
      newSpeedIndex = indexOfCurrentSpeed + 1;
    }

    const newSpeedValue = options[newSpeedIndex].value;

    dispatch("setSpeed", newSpeedValue);

  },
  setSpeed: ({ commit }, speed) => {
    commit("setSpeed", speed);
  },
  book: ({ commit, dispatch }, payload) => {
    commit("book", { loading: true });

    return api.books.index(payload)
      .then(({
        book,
        chapters,
        current_chapter_id // eslint-disable-line camelcase
      }) => {
        commit("book", {
          error: null,
          id: book.id,
          slug: book.slug,
          title: book.title,
          author: book.author,
          cover: book.cover,
          isFreeDaily: book.freeDaily,
          chapters
        });

        dispatch("getTextMarkers", book.id);
        const currentChapterId = current_chapter_id || chapters[0].id; // eslint-disable-line camelcase

        return dispatch("chapter", currentChapterId);
      })
      .catch((error) => {
        commit("book", { error });
        throw error;
      })
      .finally(() => {
        commit("book", { loading: false });
      });
  },
  chapter: ({ commit }, chapterId) => {
    commit("chapter", { loading: true });
    api.books.chapter({
      bookId: state.book.id,
      bookSlug: state.book.slug,
      chapterId: chapterId
    })
      .then((body) => {
        commit("chapter", { changing: true });

        setTimeout(() => {
          commit("chapter", {
            id: body.id,
            title: body.action_title,
            text: body.text,
            audioUrl: body.signed_audio_url
          });

          commit("chapter", { loading: false, changing: false });
        }, 300);

      })
      .catch(error => {
        commit("book", { error });
        commit("chapter", { loading: false, changing: false });
        throw error;
      });

    if (!globals.store.getters.isAnonymous) {
      api.libraryEntries.update({
        currentChapterId: chapterId,
        bookId: state.book.id,
        isFinished: false
      })
        .catch(error => {
          notify(error);
        });
    }
  },
  markAsFinished({ commit }) {
    api.libraryEntries.update({
      currentChapterId: state.chapter.id,
      bookId: state.book.id,
      isFinished: true
    })
      .catch(error => {
        commit("book", { error });

        notify(error);
      });
  },
  updateFontSize: ({ commit }, fontSize) => {
    commit("setFontSize", fontSize);
  },
  getTextMarkers: async ({ commit }, bookId) => {
    try {
      const highlights = await api.textMarkers.getTextMarkers({ bookId }) || [];

      if (Array.isArray(highlights)) {
        commit("setHighlights", highlights);
      }

    } catch (e) {
      notify(e);
    }
  },
  saveTextHighlight: async ({ state, dispatch, commit }, highlight) => {
    let payload = {
      "text_marker[id]": Math.random().toString(36).slice(2),
      "text_marker[char_from]": highlight.char_from,
      "text_marker[char_to]": highlight.char_to,
      "text_marker[book_id]": state.book.id,
      "text_marker[chapter_id]": state.chapter.id
    };
    commit("addTemporaryHighlight", highlight);

    await api.textMarkers.saveTextMarkers(payload);
    dispatch("getTextMarkers", state.book.id);
  },
  removeTextHighlight: async ({ state, dispatch, commit }, textMarkerId) => {
    commit("removeHighlightTemporariliy", textMarkerId);

    await api.textMarkers.removeTextMarkers({ textMarkerId });
    dispatch("getTextMarkers", state.book.id);
  }
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions
};
